<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToCheckbox'
});
</script>
<template>
	<span v-dis="disabled" class="to-checkbox" :class="setClass" :style="setStyle" @click.stop="toggle">
		<span class="to-checkbox-box"></span>
		<span v-if="label" class="to-checkbox-label">{{ label }}</span>
	</span>
</template>

<script setup lang="ts">
import { ProvideType } from '../../checkboxes/src/Checkboxes.vue'
import { ref, computed, watch, onBeforeUnmount, inject, onMounted } from 'vue'
import { vDis } from '@touchvue/directives'
// import dis from 'packages/directives/dis'



const ToCheckboxes = inject<ProvideType | null>('ToCheckboxes', null)

export interface ToCheckboxProps {
	joinGroup?: boolean
	onValue?: number | string | boolean
	offValue?: number | string | boolean
	label?: string
	value?: number | boolean | string
	modelValue?: number | boolean | string
	color?: string
	mode?: string
	width?: number | string
	wrap?: boolean
	observe?: boolean
	disabled?: boolean
}

const props = withDefaults(defineProps<ToCheckboxProps>(), {
	joinGroup: true,
	onValue: true,
	offValue: false,
	label: '',
	value: false,
	modelValue: undefined,
	color: 'default',
	mode: 'default',
	width: '',
	wrap: false,
	observe: false,
	disabled: false
})

const emit = defineEmits(['change', 'click', 'update:modelValue'])

// 响应式数据
// 优先使用modelValue，如果没有则使用value
const currentValue = computed(() => (props.modelValue !== undefined ? props.modelValue : props.value))
const checked = ref<boolean>(currentValue.value === props.onValue)
const valueInGroup = ref<string | number>('')
const idInGroup = ref<number | null>(null)
const disabled = ref<boolean>(props.disabled)

const parentCheckboxesValue = computed(() => ToCheckboxes?.value.value || '')

// 计算属性
const setClass = computed(() => {
	// let arr = ['to-form-disabled']
	const arr = ['to-disable-target']
	if (checked.value) {
		arr.push('is-checked')
	}
	if (props.wrap) {
		arr.push('is-wrap')
	}
	if (props.disabled || disabled.value) {
		arr.push('is-disabled')
	}
	if (props.color) {
		arr.push(`color-${props.color}`)
	}
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	return arr
})

const setStyle = computed(() => {
	const obj: any = {}
	if (props.width) {
		if (!isNaN(Number(props.width))) {
			obj.width = props.width + 'em'
		} else if (props.width === 'full') {
			obj.width = '100%'
		} else {
			obj.width = props.width
		}
	}
	return obj
})

// 监听器
watch(
	() => props.disabled,
	val => {
		disabled.value = val
		updateGroup(props.label, props.value)
	}
)

// 监听modelValue变化
watch(
	() => props.modelValue,
	val => {
		if (props.modelValue !== undefined) {
			if (val === props.onValue) {
				checked.value = true
			} else if (val === props.offValue) {
				checked.value = false
			}
		}
	}
)

// 监听value变化（用于与checkboxes搭配）
watch(
	() => props.value,
	(val, oldVal) => {
		if (!ToCheckboxes) {
			// 如果没有modelValue，则使用value
			if (props.modelValue === undefined) {
				if (val === props.onValue) {
					checked.value = true
				} else if (val === props.offValue) {
					checked.value = false
				}
			}
		} else {
			updateGroup(props.label, val)
			// 解决 unshift 时如果前面有一个勾选的，会自动勾选的问题
			ToCheckboxes.options.value.map((item: any) => {
				if (val === item.value) {
					checked.value = item.checked
				}
			})
			ToCheckboxes.removeItemValue(oldVal as any)
		}
	}
)

watch(
	() => props.label,
	val => {
		updateGroup(val, props.value)
	}
)

watch(
	() => parentCheckboxesValue.value,
	val => {
		if (val && ToCheckboxes) {
			const value = val.split(',').indexOf(String(valueInGroup.value)) > -1
			checked.value = value
			ToCheckboxes.options.value.map((item: any) => {
				if (item.id === idInGroup.value) {
					item.checked = value
				}
			})
			// 保持 checks 与 options 一致
			ToCheckboxes.checks.value = ToCheckboxes.options.value.filter((item: any) => item.checked)
		} else {
			checked.value = false
		}
	},
	{
		immediate: true
	}
)

// 方法
const toggle = () => {
	if (ToCheckboxes?.props.beforeChange) {
		const fn = ToCheckboxes.props.beforeChange()
		if (fn && typeof (fn as any).then === 'function') {
			;(fn as Promise<boolean>)
				.then(() => {
					toggleAction()
				})
				.catch(error => {
					console.log(error)
				})
		} else {
			if (fn) toggleAction()
		}
	} else {
		toggleAction()
	}
}

const toggleAction = () => {
	if (!(disabled.value || props.disabled)) {
		checked.value = !checked.value
		const newValue = checked.value ? props.onValue : props.offValue

		// 发出事件
		emit('change', newValue)
		emit('click', newValue)

		// 如果使用v-model，发出update:modelValue事件
		if (props.modelValue !== undefined) {
			emit('update:modelValue', newValue)
		}

		if (ToCheckboxes) {
			ToCheckboxes.options.value.map((item: any) => {
				if (item.id === idInGroup.value) {
					item.checked = checked.value
				}
			})
			ToCheckboxes.change(checked.value, props.value as any, props.label || '')
		}
	}
}

const updateGroup = (label: string, value: any) => {
	if (ToCheckboxes) {
		ToCheckboxes.options.value.forEach((el: any) => {
			if (el.id === idInGroup.value) {
				el.label = label
				el.value = value
				el.checked = false
				el.disabled = props.disabled
			}
		})
		if (value !== undefined && (typeof value === 'string' || typeof value === 'number')) {
			valueInGroup.value = value
		} else if (label) {
			valueInGroup.value = label
		}
	}
}

const setDisabled = (f: boolean) => {
	disabled.value = f
}

// 初始化逻辑
if (props.modelValue !== undefined) {
	// 如果有modelValue，优先使用modelValue
	checked.value = props.modelValue === props.onValue
} else if (typeof props.value === 'boolean') {
	// 否则使用value
	checked.value = props.value
}

// 初始化时注册到父组件

onMounted(() => {
	// 通过parent查找ToCheckboxes父组件
	if (props.joinGroup && ToCheckboxes) {
		if (props.value !== undefined && (typeof props.value === 'string' || typeof props.value === 'number')) {
			valueInGroup.value = props.value
		} else if (props.label) {
			valueInGroup.value = props.label
		}
		ToCheckboxes.pushOptions({
			id: ToCheckboxes.maxId.value + 1,
			value: valueInGroup.value,
			label: props.label,
			checked: checked.value,
			disabled: props.disabled
		})
		ToCheckboxes.maxId.value = idInGroup.value = ToCheckboxes.maxId.value + 1
		if (parentCheckboxesValue.value.indexOf(String(valueInGroup.value)) > -1) {
			checked.value = true
		}
	}
})

// 清理
onBeforeUnmount(() => {
	if (ToCheckboxes) {
		ToCheckboxes.removeItemValue(props.value as any)
	}
})

// 暴露方法
defineExpose({
	toggle,
	updateGroup,
	setDisabled
})
</script>
